"use client"

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const API_URL = window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const LoadingSpinner = ({ text = "読み込み中..." }) => (
    <div className="flex items-center text-lg font-semibold text-gray-700">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {text}
    </div>
);

const CreditCardIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
        <line x1="1" y1="10" x2="23" y2="10"></line>
    </svg>
);

const formatCurrency = (amount) => {
    const num = Number(amount);
    return Number.isFinite(num) ? new Intl.NumberFormat('ja-JP').format(num) : '0';
};

const getIntervalLabel = (interval, count) => {
    const intervalCount = Math.max(1, Number(count) || 1);
    const unit = interval === 'year' ? '年' : 'ヶ月';
    return intervalCount === 1 ? `毎${unit}` : `${intervalCount}${unit}ごと`;
};

function SubscriptionViewPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [subscription, setSubscription] = useState(null);
    const [oemBranding, setOemBranding] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPaying, setIsPaying] = useState(false);

    const statusMessage = useMemo(() => {
        const status = searchParams.get('status');
        if (status === 'success') return 'お申し込みを受け付けました。決済状況の反映まで少し時間がかかる場合があります。';
        if (status === 'cancel') return '決済画面を閉じました。お申し込みはまだ完了していません。';
        return null;
    }, [searchParams]);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            setError("申込リンクが見つかりません。");
            return;
        }

        const fetchSubscription = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_URL}/api/public/subscription/${token}`);
                const data = await res.json();
                if (!res.ok || !data.success) {
                    throw new Error(data.error || "サブスクリプション情報を取得できませんでした。");
                }
                setSubscription(data.subscription);
                setOemBranding(data.oemBranding || null);
            } catch (err) {
                setError(err.message || "サブスクリプション情報の取得に失敗しました。");
            } finally {
                setLoading(false);
            }
        };

        fetchSubscription();
    }, [token]);

    useEffect(() => {
        if (!subscription) return;
        const brandName = oemBranding?.display_name || subscription.issuer_company_name || 'Sales Breaker';
        document.title = `${brandName} | サブスクリプション申込`;
        if (oemBranding?.logo_path) {
            const iconUrl = `${API_URL}/api/oem${oemBranding.logo_path}`;
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            link.href = iconUrl;
        }
    }, [subscription, oemBranding]);

    const handleCheckout = async () => {
        try {
            setIsPaying(true);
            const res = await fetch(`${API_URL}/api/public/subscription/${token}/checkout`, { method: 'POST' });
            const data = await res.json();
            if (!res.ok || !data.success || !data.url) {
                throw new Error(data.error || "決済画面の生成に失敗しました。");
            }
            window.location.href = data.url;
        } catch (err) {
            alert(err.message || "通信エラーが発生しました。");
        } finally {
            setIsPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <LoadingSpinner text="申込内容を読み込んでいます..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
                    <h2 className="text-xl font-bold text-red-600 mb-4">エラー</h2>
                    <p className="text-gray-700">{error}</p>
                    <p className="text-sm text-gray-500 mt-4">このリンクが共有されたものである場合は、送信元にご確認ください。</p>
                </div>
            </div>
        );
    }

    const status = String(subscription?.status || '').toLowerCase();
    const isAlreadyActive = ['active', 'trialing'].includes(status);
    const isCanceled = ['canceled', 'cancelled'].includes(status);
    const intervalLabel = getIntervalLabel(subscription.interval_unit, subscription.interval_count);
    const brandColor = oemBranding?.brand_color || '#2563eb';

    return (
        <div className="min-h-screen bg-gray-100 p-3 sm:p-6 md:p-10">
            <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
                <div className="p-5 sm:p-8 border-b">
                    <p className="text-xs font-bold text-gray-400 tracking-widest uppercase">Subscription</p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">サブスクリプション申込</h1>
                    <p className="text-gray-600 mt-2">{subscription.customer_company_name || '(お客様)'} 御中</p>
                </div>

                <div className="p-5 sm:p-8 space-y-6">
                    {statusMessage && (
                        <div className={`rounded-lg border p-4 text-sm font-bold ${searchParams.get('status') === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                            {statusMessage}
                        </div>
                    )}

                    <div className="rounded-xl bg-gray-50 p-5 sm:p-6">
                        <p className="text-sm font-bold text-gray-500">申込内容</p>
                        <h2 className="text-xl font-black text-gray-900 mt-2">{subscription.name}</h2>
                        <div className="mt-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                            <div>
                                <p className="text-sm text-gray-500">料金</p>
                                <p className="text-4xl font-black text-gray-900 mt-1">¥ {formatCurrency(subscription.amount)}</p>
                                <p className="text-sm text-gray-500 mt-1">{intervalLabel}</p>
                            </div>
                            {Number(subscription.trial_days) > 0 && (
                                <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-blue-700 text-sm font-bold">
                                    初回 {subscription.trial_days} 日間トライアル
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                        <div>
                            <p className="font-bold text-gray-500 mb-1">販売元</p>
                            <p className="font-bold text-gray-900">{subscription.issuer_company_name || '-'}</p>
                            {subscription.issuer_address && <p className="text-gray-600 mt-1">{subscription.issuer_address}</p>}
                            {subscription.issuer_phone && <p className="text-gray-600 mt-1">TEL: {subscription.issuer_phone}</p>}
                        </div>
                        <div>
                            <p className="font-bold text-gray-500 mb-1">お支払い方法</p>
                            <p className="text-gray-700">クレジットカード決済</p>
                            <p className="text-xs text-gray-400 mt-1">Stripeの安全な決済画面へ移動します。</p>
                        </div>
                    </div>

                    <div className="border-t pt-6">
                        {isAlreadyActive ? (
                            <div className="rounded-xl bg-green-50 border border-green-200 p-5 text-green-700 font-bold text-center">
                                このサブスクリプションは申込済みです。
                            </div>
                        ) : isCanceled ? (
                            <div className="rounded-xl bg-gray-100 border border-gray-200 p-5 text-gray-600 font-bold text-center">
                                このサブスクリプション申込リンクは無効です。
                            </div>
                        ) : (
                            <button
                                onClick={handleCheckout}
                                disabled={isPaying}
                                className="w-full inline-flex items-center justify-center gap-3 text-white font-black py-4 px-8 rounded-xl shadow-xl transition-all active:scale-95 disabled:bg-gray-400"
                                style={{ backgroundColor: isPaying ? '#9ca3af' : brandColor }}
                            >
                                <CreditCardIcon />
                                {isPaying ? "準備中..." : "カードで申し込む"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SubscriptionViewPage;
